import React, {ReactNode} from 'react'
import {
    LeftColumnFlexContainer,
    LeftTopRowFlexContainer,
    RightColumnFlexContainer,
} from '../../../styledComponents/FlexComponents'
import {Nullable} from '../../../app/TypeHelpers'
import {Box, Card, CardActions, CardMedia} from '@mui/material';
import {DeleteLink} from '../../../styledComponents/adminPage/Links'
import styled from 'styled-components'
import {RequestItemError} from '../../../network/response/RequestItemError'
import {StyledInput} from '../../../styledComponents/StyledTypeDefinitions'
import PPErrorDisplayButton from './PPErrorDisplayButton'
import {HintProps} from './hoc/withHintButton'
import {ErrorMessageProps} from './hoc/withErrorMessage'
import {createUUID} from '../../../stringHelper/stringHelpers'
import md5 from 'md5'
import EventEmitterExAdmin from '../pageBaseComponents/EventEmitterExAdmin'

export interface ImageInformation {
    fileNumber: Nullable<number>
    url: string
    name: Nullable<string>
    size: Nullable<number>
    base64data: Nullable<string>
    md5: Nullable<string>
}

interface CustomErrorDisplayProps {
    hasError?: boolean
    eventEmitter?: EventEmitterExAdmin
    eventKeys?: string[]
}

interface Props {
    images: ImageInformation[]
    maxCount: number
    buttonLabel?: string

    inputProps?: React.InputHTMLAttributes<HTMLInputElement>

    helperText?: ReactNode
    maxFileSizeBytesPerFile?: number
    maxFileSizeBytesPerFileMessage?: string
    maxFileSizeBytesTotal?: number
    maxFileSizeBytesTotalMessage?: string
    onChange?: (images: ImageInformation[]) => void
}

interface State {
    clearError: boolean
}

const EVENT_KEY_OVER_MAX_FILESIZE: string = 'PPImageInputWithPreviewOverMaxFileSize'

class PPImageInputWithPreview
    extends React.Component<Props & CustomErrorDisplayProps & HintProps & ErrorMessageProps, State> {

    constructor(props: Readonly<Props & CustomErrorDisplayProps & HintProps & ErrorMessageProps>) {
        super(props)

        this.state = {
            clearError: false,
        }

        this.handleChange = this.handleChange.bind(this)
        this.handleDelete = this.handleDelete.bind(this)
    }

    async getBinaryString(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            let binary: string = ''
            const reader = new FileReader()
            reader.onload = () => {
                if (!reader.result) {
                    reject('reader.result is null')
                }
                const bytes = new Uint8Array(reader.result as ArrayBuffer)
                const length = bytes.byteLength
                for (let i: number = 0; i < length; i++) {
                    const tmpByte = bytes[i]
                    binary += String.fromCharCode(tmpByte)
                }
                resolve(binary)
            }
            reader.readAsArrayBuffer(file)
        })
    }

    validateUploadFileSize(file: File): Nullable<RequestItemError> {
        const {
            maxFileSizeBytesPerFile, maxFileSizeBytesPerFileMessage,
            maxFileSizeBytesTotal, maxFileSizeBytesTotalMessage,
            images,
        } = this.props

        if (maxFileSizeBytesPerFile && file.size > maxFileSizeBytesPerFile) {
            return new RequestItemError(
                EVENT_KEY_OVER_MAX_FILESIZE,
                maxFileSizeBytesPerFileMessage || '1ファイルあたりの上限サイズを超えています',
                '',
            )
        }

        if (maxFileSizeBytesTotal) {
            const totalSize: number = images.reduce((total, image) => total + (image.size || 0), 0)
            if (totalSize + file.size > maxFileSizeBytesTotal) {
                return new RequestItemError(
                    EVENT_KEY_OVER_MAX_FILESIZE,
                    maxFileSizeBytesTotalMessage || 'アップロードファイル総上限サイズを超えています',
                    '',
                )
            }
        }
        return null
    }

    async handleChange(event: React.ChangeEvent<HTMLInputElement>): Promise<void> {
        this.setState({clearError: false})
        if (!this.props.onChange) {
            return
        }
        if (event.target.files === null || event.target.files.length === 0 || event.target.files[0] === null) {
            return
        }

        const file = event.target.files[0]
        const error = this.validateUploadFileSize(file)
        if (error) {
            this.setState({clearError: false}, () => {
                if (this.props.eventEmitter) {
                    this.props.eventEmitter.emit(EVENT_KEY_OVER_MAX_FILESIZE, error)
                }
            })
            return
        }

        const binaryString: string = await this.getBinaryString(file)
        const charArray: number[] = (binaryString).split('').map(s => s.charCodeAt(0))
        const md5String: string = md5(charArray)
        this.props.onChange([
            ...this.props.images,
            {
                fileNumber: null,
                url: URL.createObjectURL(file),
                name: file.name,
                base64data: btoa(binaryString),
                size: file.size,
                md5: md5String,
            },
        ])
    }

    renderNoImages(): ReactNode {
        const noImageCount = this.props.maxCount - this.props.images.length
        if (noImageCount === 0) {
            return null
        }
        return new Array(noImageCount)
            .fill('')
            .map((url, index) =>
                <Box
                    ml={(noImageCount === this.props.maxCount && index === 0) ? '0' : '10px'}
                    key={index}
                >
                    <PreviewImage src={url}/>
                </Box>)
    }

    handleDelete = (index: number) => (): void => {
        this.setState({clearError: true})
        if (!this.props.onChange) {
            return
        }
        this.props.onChange(this.props.images.filter((image, i) => index !== i))
    }

    render(): ReactNode {
        const id: string = createUUID()
        const {inputProps, images, maxCount, hint, eventEmitter, eventKeys, buttonLabel, helperText} = this.props
        const disableToSelectFile: boolean = (images.length >= maxCount)

        return <>
            <LeftColumnFlexContainer>
                <InvisibleInput
                    {...inputProps}
                    id={id}
                    type="file"
                    value={''}
                    multiple={false}
                    disabled={disableToSelectFile}
                    onChange={this.handleChange}
                />
                <label htmlFor={id}>
                    <PPErrorDisplayButton
                        component={'span'}
                        disabled={disableToSelectFile}
                        size={'small'}
                        hint={hint}
                        eventEmitter={eventEmitter}
                        eventKeys={[...(eventKeys || []), EVENT_KEY_OVER_MAX_FILESIZE]}
                        onClick={() => {
                            this.setState({clearError: false})
                        }}
                        clearError={this.state.clearError}
                    >
                        {buttonLabel || '画像を選択'}
                    </PPErrorDisplayButton>
                </label>
                {helperText}
                <div>
                    <LeftTopRowFlexContainer>
                        {
                            images.map((image, index) => {
                                return (
                                    <Box
                                        ml={(index !== 0) ? '10px' : undefined}
                                        key={index}
                                    >
                                        <PreviewImage
                                            src={image.url}
                                            onDelete={this.handleDelete(index)}
                                        />
                                    </Box>
                                )
                            })
                        }
                        {this.renderNoImages()}
                    </LeftTopRowFlexContainer>
                </div>
            </LeftColumnFlexContainer>
        </>;
    }
}

const InvisibleInput: StyledInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;
  appearance: none;
  position: absolute;
`

interface PreviewImageProps {
    src: string
    onDelete?: () => void
}

const PreviewImage: React.FC<PreviewImageProps> = props => {
    const imageExist: boolean = (props.src.length > 0)
    const imageSrc: string = imageExist
        ? props.src
        : `/resources/images/upload/img_noupload.png`

    return (
        <Card>
            <StyledCardContent>
                <CardMedia
                    style={{height: '150px' , width:'150px' , objectFit:'contain'}}
                    component={'img'}
                    src={imageSrc}
                />
            </StyledCardContent>
            {
                imageExist &&
                <RightColumnFlexContainer>
                    <CardActions>
                        <DeleteLink onClick={(event: React.MouseEvent) => {
                            event.preventDefault()
                            if (props.onDelete) {
                                props.onDelete()
                            }
                        }}>削除</DeleteLink> </CardActions>
                </RightColumnFlexContainer>
            }
        </Card>
    )
}


const StyledCardContent = styled.div`
  background-color: #EEEEEE;
  width: 150px;
  height: 150px;
  display: flex;
  justify-content: center;
  align-items: center;
`

export default PPImageInputWithPreview
